'use client';

import { useState, useEffect } from 'react';
import { useDarkMode } from '@/components/DarkModeProvider';
import Footer from '@/components/Footer';
import HeroSection from './HeroSection';
import PlatformSection from './PlatformSection';
import FeaturedTemplates from './FeaturedTemplates';
import SocialProofSection from './SocialProofSection';
import HowItWorksSection from './HowItWorksSection';
import { supabase } from '@/lib/supabaseClient';
import PopularTemplates from './PopularTemplates';

// In-memory cache
interface StatsCache {
  data: DynamicStat[];
  timestamp: number;
}
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let statsCache: StatsCache | null = null;

interface DynamicStat {
  value: string;
  label: string;
  icon: string;
  isStatic?: boolean;
  numericValue?: number;
}

interface PlatformCounts {
  shopify: number;
  wordpress: number;
  woocommerce: number;
  html: number;
  nextjs: number;
  webflow: number;
}

interface FeaturedTemplate {
  id: string;
  name: string;
  platform: string;
  category: string | null;
  price: number;
  rating: number;
  reviews_count: number;
  downloads_count: number;
  thumbnail_url: string | null;
  tags: string[];
}

interface HomepageStats {
  premiumTemplates: number;
  happyCustomers: number;
  platformsSupported: number;
  averageRating: string;
}

export default function HomepageClient() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [stats, setStats] = useState<HomepageStats>({
    premiumTemplates: 0,
    happyCustomers: 0,
    platformsSupported: 0,
    averageRating: '0.0/5',
  });
  const [monthlySalesCount, setMonthlySalesCount] = useState<number>(0);
  const [growthPercent, setGrowthPercent] = useState<number | null>(null);
  const [showGrowth, setShowGrowth] = useState<boolean>(false);
  const [showSalesWidget, setShowSalesWidget] = useState<boolean>(false);
  const [platformCounts, setPlatformCounts] = useState<PlatformCounts>({
    shopify: 0,
    wordpress: 0,
    woocommerce: 0,
    html: 0,
    nextjs: 0,
    webflow: 0,
  });
  const [featuredTemplates, setFeaturedTemplates] = useState<FeaturedTemplate[]>([]);
  const [dynamicStats, setDynamicStats] = useState<DynamicStat[] | undefined>(undefined);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchHomepageStats = async () => {
      // 1. Premium Templates Count
      const { count: templatesCount } = await supabase
        .from('templates')
        .select('*', { count: 'exact', head: true });

      // 2. Happy Customers Count (unique user_ids from purchases where status = 'paid')
      const { data: uniqueCustomers } = await supabase
        .from('purchases')
        .select('buyer_id')
        .eq('status', 'paid');

      const uniqueCustomerCount = uniqueCustomers
        ? new Set(uniqueCustomers.map((p) => p.buyer_id)).size
        : 0;

      // 3. Platforms Supported (distinct platform values from templates)
      const { data: platforms } = await supabase
        .from('templates')
        .select('platform');

      const uniquePlatforms = platforms
        ? new Set(platforms.map((t) => t.platform)).size
        : 0;

      // 4. Average Rating (from reviews table)
      const { data: reviews } = await supabase
        .from('reviews')
        .select('rating');

      let avgRating = 0;
      if (reviews && reviews.length > 0) {
        const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
        avgRating = totalRating / reviews.length;
      }

      setStats({
        premiumTemplates: templatesCount || 0,
        happyCustomers: uniqueCustomerCount,
        platformsSupported: uniquePlatforms,
        averageRating: `${avgRating.toFixed(1)}/5`,
      });
    };

    const fetchDynamicStats = async (background = false) => {
      if (!background) setStatsLoading(true);
      try {
        // A) Premium Templates
        const { count: templatesCount, error: templatesError } = await supabase
          .from('templates')
          .select('*', { count: 'exact', head: true });
        if (templatesError) console.error('[Stats] templates error:', templatesError);

        // B) Happy Customers (unique buyer_ids from purchases where status = 'paid')
        const { data: purchasesData, error: purchasesError } = await supabase
          .from('purchases')
          .select('buyer_id')
          .eq('status', 'paid');
        if (purchasesError) console.error('[Stats] purchases error:', purchasesError);
        const uniqueCustomers = purchasesData
          ? new Set(purchasesData.map((p: { buyer_id: string }) => p.buyer_id)).size
          : 0;

        // C) Average Rating
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('rating');
        if (reviewsError) console.error('[Stats] reviews error:', reviewsError);
        let avgRating = 0;
        if (reviewsData && reviewsData.length > 0) {
          const total = reviewsData.reduce((sum: number, r: { rating: number }) => sum + r.rating, 0);
          avgRating = total / reviewsData.length;
        }

        const premiumCount = templatesCount ?? 0;
        const result: DynamicStat[] = [
          {
            value: premiumCount.toLocaleString() + '+',
            label: 'Premium Templates',
            icon: 'RectangleStackIcon',
            numericValue: premiumCount,
          },
          {
            value: uniqueCustomers.toLocaleString() + '+',
            label: 'Happy Customers',
            icon: 'UsersIcon',
            numericValue: uniqueCustomers,
          },
          {
            value: '6',
            label: 'Platforms Supported',
            icon: 'CubeTransparentIcon',
            isStatic: true,
          },
          {
            value: `${avgRating.toFixed(1)}/5`,
            label: 'Average Rating',
            icon: 'StarIcon',
          },
        ];

        statsCache = { data: result, timestamp: Date.now() };
        setDynamicStats(result);
      } catch (err) {
        console.error('[Stats] fetch error:', err);
        // Fallback values
        const fallback: DynamicStat[] = [
          { value: '0+', label: 'Premium Templates', icon: 'RectangleStackIcon', numericValue: 0 },
          { value: '0+', label: 'Happy Customers', icon: 'UsersIcon', numericValue: 0 },
          { value: '6', label: 'Platforms Supported', icon: 'CubeTransparentIcon', isStatic: true },
          { value: '0.0/5', label: 'Average Rating', icon: 'StarIcon' },
        ];
        if (!background) setDynamicStats(fallback);
      } finally {
        if (!background) setStatsLoading(false);
      }
    };

    const loadStats = () => {
      const now = Date.now();
      if (statsCache && now - statsCache.timestamp < CACHE_TTL) {
        // Use cached data immediately
        setDynamicStats(statsCache.data);
        setStatsLoading(false);
        // Revalidate in background
        fetchDynamicStats(true);
      } else {
        fetchDynamicStats(false);
      }
    };

    const fetchMonthlySalesCount = async () => {
      // Get first and last day of current month
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
      const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)?.toISOString();

      const { count: thisMonthCount, error: thisMonthError } = await supabase?.from('purchases')?.select('*', { count: 'exact', head: true })?.eq('status', 'paid')?.gte('purchase_date', firstDayOfMonth)?.lte('purchase_date', lastDayOfMonth);

      if (!thisMonthError && thisMonthCount !== null) {
        setMonthlySalesCount(thisMonthCount);
        setShowSalesWidget(thisMonthCount >= 20);
      }

      // Get first and last day of last month
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)?.toISOString();
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)?.toISOString();

      const { count: lastMonthCount, error: lastMonthError } = await supabase?.from('purchases')?.select('*', { count: 'exact', head: true })?.eq('status', 'paid')?.gte('purchase_date', firstDayOfLastMonth)?.lte('purchase_date', lastDayOfLastMonth);

      // Calculate growth percentage with division-by-zero handling
      if (!thisMonthError && !lastMonthError && thisMonthCount !== null && lastMonthCount !== null) {
        if (lastMonthCount === 0) {
          setGrowthPercent(null);
          setShowGrowth(false);
        } else {
          const growth = ((thisMonthCount - lastMonthCount) / lastMonthCount) * 100;
          setGrowthPercent(growth);
          setShowGrowth(thisMonthCount >= 20);
        }
      }
    };

    const fetchPlatformCounts = async () => {
      const dbPlatforms = ['shopify', 'wordpress', 'woocommerce', 'html'] as const;
      const counts: Partial<PlatformCounts> = {};

      for (const platform of dbPlatforms) {
        try {
          const { count, error } = await supabase
            .from('templates')
            .select('*', { count: 'exact', head: true })
            .eq('platform', platform)
            .eq('is_published', true);

          if (!error && count !== null) {
            counts[platform as keyof PlatformCounts] = count;
          }
        } catch {
          // Silently fall back to default (0)
        }
      }

      setPlatformCounts((prev) => ({
        ...prev,
        ...counts,
      }));
    };

    const fetchFeaturedTemplates = async () => {
      const { data, error } = await supabase
        .from('templates')
        .select('id, name, platform, category, price, rating, reviews_count, downloads_count, thumbnail_url, tags')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (!error && data) {
        setFeaturedTemplates(data as FeaturedTemplate[]);
      }
    };

    fetchHomepageStats();
    fetchMonthlySalesCount();
    fetchPlatformCounts();
    fetchFeaturedTemplates();
    loadStats();
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="flex-1">
        <HeroSection 
          stats={stats}
          monthlySalesCount={monthlySalesCount} 
          growthPercent={growthPercent} 
          showGrowth={showGrowth} 
          showSalesWidget={showSalesWidget} 
        />
        <PlatformSection platformCounts={platformCounts} />
        <FeaturedTemplates templates={featuredTemplates} />
        <PopularTemplates />
        <SocialProofSection dynamicStats={dynamicStats} statsLoading={statsLoading} />
        <HowItWorksSection />
      </main>
      <Footer />
    </div>
  );
}